package link.erpango.quip.catalog.sections

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Card
import androidx.compose.material.Divider
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.material.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import link.erpango.quip.theme.ColorSchemes
import link.erpango.quip.theme.QuipColorScheme
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.theme.QuipDimens
import link.erpango.quip.ui.components.QuipSectionHeader

@Composable
fun CatalogColorsSection() {
    val schemes = ColorSchemes.allSchemes.entries.toList()

    LazyColumn(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = QuipDimens.padding16, vertical = QuipDimens.padding12),
        verticalArrangement = Arrangement.spacedBy(QuipDimens.padding12),
    ) {
        item {
            QuipSectionHeader(title = "Color Themes (${schemes.size})")
            Spacer(Modifier.height(4.dp))
            Text(
                text = "Toca 'Aplicar' para previsualizar el tema en toda la app.",
                style = MaterialTheme.typography.caption,
                color = QuipColors.colorTextSecondary,
            )
            Spacer(Modifier.height(QuipDimens.padding8))
        }

        items(schemes) { (name, scheme) ->
            SchemeCard(name = name, scheme = scheme)
        }

        item { Spacer(Modifier.height(QuipDimens.padding16)) }
    }
}

@Composable
private fun SchemeCard(name: String, scheme: QuipColorScheme) {
    val isActive = QuipColors.currentScheme === scheme
    Card(
        modifier = Modifier.fillMaxWidth(),
        elevation = if (isActive) QuipDimens.elevationMedium else QuipDimens.elevationSmall,
        shape = RoundedCornerShape(QuipDimens.cornerRadius12),
        backgroundColor = QuipColors.colorSurface,
        border = if (isActive) androidx.compose.foundation.BorderStroke(2.dp, QuipColors.colorPrimary) else null,
    ) {
        Column(modifier = Modifier.padding(QuipDimens.padding12)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column {
                    Text(
                        text = name,
                        style = MaterialTheme.typography.subtitle1,
                        fontWeight = FontWeight.Bold,
                        color = QuipColors.colorText,
                    )
                    if (isActive) {
                        Text(
                            text = "activo",
                            style = MaterialTheme.typography.caption,
                            color = QuipColors.colorPrimary,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                }
                TextButton(
                    onClick = { QuipColors.updateScheme(scheme) },
                    enabled = !isActive,
                ) {
                    Text(
                        text = if (isActive) "✓ Activo" else "Aplicar",
                        color = if (isActive) QuipColors.colorTextSecondary else QuipColors.colorPrimary,
                        fontSize = 12.sp,
                    )
                }
            }

            Spacer(Modifier.height(QuipDimens.padding8))
            Divider(color = QuipColors.colorBackground, thickness = 1.dp)
            Spacer(Modifier.height(QuipDimens.padding8))

            // First row: primary, secondary, background, surface, text, textSecondary
            ColorSwatchRow(
                swatches = listOf(
                    "primary"    to scheme.colorPrimary,
                    "secondary"  to scheme.colorSecondary,
                    "background" to scheme.colorBackground,
                    "surface"    to scheme.colorSurface,
                    "text"       to scheme.colorText,
                    "textSecond" to scheme.colorTextSecondary,
                ),
            )
            Spacer(Modifier.height(QuipDimens.padding4))
            // Second row: gradientStart, gradientEnd, error, success, warning, onPrimary
            ColorSwatchRow(
                swatches = listOf(
                    "gradStart"  to scheme.gradientStart,
                    "gradEnd"    to scheme.gradientEnd,
                    "error"      to scheme.colorError,
                    "success"    to scheme.colorSuccess,
                    "warning"    to scheme.colorWarning,
                    "onPrimary"  to scheme.colorOnPrimary,
                ),
            )
        }
    }
}

@Composable
private fun ColorSwatchRow(swatches: List<Pair<String, Color>>) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(QuipDimens.padding4),
    ) {
        swatches.forEach { (label, color) ->
            Column(
                modifier = Modifier.weight(1f),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Box(
                    modifier = Modifier
                        .size(32.dp)
                        .clip(RoundedCornerShape(6.dp))
                        .background(color)
                        .border(1.dp, QuipColors.colorTextSecondary.copy(alpha = 0.2f), RoundedCornerShape(6.dp)),
                )
                Spacer(Modifier.width(2.dp))
                Text(
                    text = label,
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary,
                    fontSize = 8.sp,
                    maxLines = 1,
                )
            }
        }
    }
}
